import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { AutoComplete } from "primereact/autocomplete";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import ResponsiveView from "../../components/responsive/ResponsiveView";
import DataCard from "../../components/responsive/DataCard";
import PaymentAllocationPanel from "../../components/payment/PaymentAllocationPanel";
import {
    fetchOutstandingInvoices,
    formatMoney,
    generateIdempotencyKey,
    paymentRowStatus,
    PAYMENT_METHODS_BY_ROLE,
    processClientPayment,
    searchClients,
} from "../../services/clientPaymentService";
import { fetchCashRegisters } from "../../services/cashService";
import styles from "./clientPayment.module.scss";

const STEP = { SEARCH: 0, PAY: 1, DONE: 2 };

const RecordPayment = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const role = localStorage.getItem("role");
    const methodOptions = PAYMENT_METHODS_BY_ROLE[role] || [];

    // A staff member already holding a register on their own active shift
    // never needs to be asked which one a cash payment goes into - it's
    // theirs. Admins aren't tied to a shift/register, so they always choose.
    const { onShift, shift } = useSelector((s) => s.activeShiftSlice);
    const isAdmin = role === "admin";
    const myRegister = onShift && shift?.cashRegister ? shift.cashRegister : null;
    const myRegisterId = myRegister?._id || (typeof myRegister === "string" ? myRegister : null);
    const hasOwnRegister = !isAdmin && !!myRegisterId;

    const [step, setStep] = useState(STEP.SEARCH);
    const [searchQuery, setSearchQuery] = useState("");
    const [clientSuggestions, setClientSuggestions] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const searchTimeout = useRef(null);

    const [flightNumber, setFlightNumber] = useState("");
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [purchaseInvoiceCount, setPurchaseInvoiceCount] = useState(0);
    const [unattachedPurchaseCount, setUnattachedPurchaseCount] = useState(0);
    const [totalOutstanding, setTotalOutstanding] = useState(0);
    const [accountCreditBalance, setAccountCreditBalance] = useState(0);
    const [applyCredit, setApplyCredit] = useState(false);

    const [paymentMethod, setPaymentMethod] = useState(methodOptions[0]?.value || "");
    const [submitting, setSubmitting] = useState(false);
    const [idempotencyKey, setIdempotencyKey] = useState(generateIdempotencyKey());
    const [result, setResult] = useState(null);
    const [panelKey, setPanelKey] = useState(0);

    const [cashRegisters, setCashRegisters] = useState([]);
    const [cashRegisterId, setCashRegisterId] = useState(hasOwnRegister ? myRegisterId : null);

    useEffect(() => {
        fetchCashRegisters().then((res) => setCashRegisters(res?.data || []));
    }, []);

    useEffect(() => {
        if (hasOwnRegister) setCashRegisterId(myRegisterId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasOwnRegister, myRegisterId]);
    const cashRegisterOptions = cashRegisters.map((r) => ({
        label: r.inUseBy ? `${r.name} (currently held by ${r.inUseBy})` : r.name,
        value: r._id,
    }));

    const handleSearchInput = (e) => {
        const q = e.query;
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        if (!q || q.trim().length < 2) {
            setClientSuggestions([]);
            return;
        }
        searchTimeout.current = setTimeout(async () => {
            const res = await searchClients(q.trim());
            const withDisplay = (res?.data || []).map((c) => ({
                ...c,
                displayName: `${c.firstName} ${c.lastName} - ${c.email}`,
            }));
            setClientSuggestions(withDisplay);
        }, 300);
    };

    const loadInvoicesForClient = async (client, flight) => {
        setLoadingInvoices(true);
        const res = await fetchOutstandingInvoices(client._id, flight);
        if (res?.success) {
            setInvoices(res.data.invoices || []);
            setPurchaseInvoiceCount(res.data.purchaseInvoiceCount || 0);
            setUnattachedPurchaseCount(res.data.unattachedPurchaseCount || 0);
            setTotalOutstanding(res.data.totalOutstanding || 0);
            setAccountCreditBalance(res.data.accountCreditBalance || 0);
        }
        setPanelKey((k) => k + 1);
        setLoadingInvoices(false);
    };

    const handleSelectClient = async (client) => {
        setSelectedClient(client);
        setApplyCredit(false);
        await loadInvoicesForClient(client, flightNumber);
        setStep(STEP.PAY);
    };

    const handleFlightFilter = () => {
        if (selectedClient) loadInvoicesForClient(selectedClient, flightNumber);
    };

    const handlePanelAction = (snapshot) => {
        if (!paymentMethod) return;

        const nativeAmountLabel = snapshot.currency === "jmd" ? `J$${Number(snapshot.amountReceived).toFixed(2)} (${formatMoney(snapshot.totalAvailable)} USD)` : formatMoney(snapshot.totalAvailable);
        confirmDialog({
            message: `Apply ${nativeAmountLabel} to ${selectedClient?.firstName} ${selectedClient?.lastName}'s outstanding invoices? This can't be undone.`,
            header: "Confirm Payment",
            icon: "pi pi-question-circle",
            accept: async () => {
                setSubmitting(true);
                try {
                    const res = await processClientPayment(dispatch, {
                        idempotencyKey,
                        clientId: selectedClient._id,
                        flightNumberFilter: flightNumber,
                        amountReceived: Number(snapshot.amountReceived),
                        currency: snapshot.currency,
                        paymentMethod,
                        applyCredit,
                        mode: snapshot.mode,
                        ...(snapshot.selections ? { selections: snapshot.selections } : {}),
                        ...(paymentMethod === "cash" ? { cashRegisterId: snapshot.cashRegisterId } : {}),
                    });
                    if (res?.success) {
                        setResult(res.data);
                        setStep(STEP.DONE);
                    }
                } finally {
                    setSubmitting(false);
                }
            },
        });
    };

    const startOver = () => {
        setStep(STEP.SEARCH);
        setSearchQuery("");
        setClientSuggestions([]);
        setSelectedClient(null);
        setFlightNumber("");
        setInvoices([]);
        setApplyCredit(false);
        setCashRegisterId(hasOwnRegister ? myRegisterId : null);
        setPanelKey((k) => k + 1);
        setIdempotencyKey(generateIdempotencyKey());
        setResult(null);
    };

    const statusBody = (row) => {
        const { label, badgeClass } = paymentRowStatus(row);
        return <span className={`${styles.badge} ${styles[badgeClass]}`}>{label}</span>;
    };

    const allocationCard = (row, i) => (
        <DataCard
            key={row.invoiceId || i}
            accent={row.fullyPaid ? "green" : "slate"}
            title={row.invoiceId || row.invoiceNo || "-"}
            subtitle={row.Waybills}
            badge={statusBody(row)}
            fields={[
                { label: "Flight #", value: row.shipmentNumber },
                { label: "Balance Before", value: formatMoney(row.balanceBefore) },
                { label: "Applied", value: formatMoney(row.amountApplied) },
                { label: "Balance After", value: formatMoney(row.balanceAfter) },
            ]}
        />
    );

    return (
        <div className={styles.page}>
            <ConfirmDialog />

            <div className={styles.backLink} onClick={() => history.push("/invoices")}>
                <i className="pi pi-arrow-left" /> Back to Invoices
            </div>

            <div className={styles.pageHeader}>
                <div className={styles.headerTitleRow}>
                    <div className={styles.headerIcon}>
                        <i className="pi pi-wallet" />
                    </div>
                    <div>
                        <h2>Record Payment</h2>
                        <p className={styles.subtitle}>Apply a wire or cash payment across a client's outstanding invoices</p>
                    </div>
                </div>
            </div>

            <div className={styles.stepsNav}>
                <div className={`${styles.stepPill} ${step === STEP.SEARCH ? styles.stepActive : step > STEP.SEARCH ? styles.stepDone : ""}`}>
                    <span className={styles.stepNum}>1</span> Find Client
                </div>
                <i className={`pi pi-angle-right ${styles.stepArrow}`} />
                <div className={`${styles.stepPill} ${step === STEP.PAY ? styles.stepActive : step > STEP.PAY ? styles.stepDone : ""}`}>
                    <span className={styles.stepNum}>2</span> Review &amp; Pay
                </div>
                <i className={`pi pi-angle-right ${styles.stepArrow}`} />
                <div className={`${styles.stepPill} ${step === STEP.DONE ? styles.stepActive : ""}`}>
                    <span className={styles.stepNum}>3</span> Done
                </div>
            </div>

            {step === STEP.SEARCH && (
                <div className={styles.sectionCard}>
                    <div className={styles.sectionCardHeader}>
                        <h3>
                            <i className="pi pi-search" />
                            Find the Client
                        </h3>
                    </div>
                    <div className={styles.field}>
                        <label>Client Name</label>
                        <AutoComplete
                            value={searchQuery}
                            suggestions={clientSuggestions}
                            completeMethod={handleSearchInput}
                            field="displayName"
                            onChange={(e) => setSearchQuery(typeof e.value === "string" ? e.value : e.value.displayName)}
                            onSelect={(e) => handleSelectClient(e.value)}
                            placeholder="Start typing a client's name or email..."
                            style={{ width: "100%" }}
                            inputStyle={{ width: "100%" }}
                            panelStyle={{ maxWidth: "480px" }}
                            emptyMessage="No clients found"
                        />
                    </div>
                </div>
            )}

            {step === STEP.PAY && selectedClient && (
                <>
                    <div className={styles.selectedClientBanner}>
                        <div className={styles.clientInfo}>
                            <div className={styles.clientAvatar}>{(selectedClient.firstName || "?")[0]}</div>
                            <div>
                                <strong>
                                    {selectedClient.firstName} {selectedClient.lastName}
                                </strong>
                                <br />
                                <span>{selectedClient.email}</span>
                            </div>
                        </div>
                        <Button label="Change Client" icon="pi pi-refresh" className={styles.secondaryButton} onClick={startOver} />
                    </div>

                    <div className={styles.sectionCard}>
                        <div className={styles.sectionCardHeader}>
                            <h3>
                                <i className="pi pi-filter" />
                                Filter by Flight Number (optional)
                            </h3>
                        </div>
                        <div className={styles.searchRow}>
                            <div className={styles.field}>
                                <InputText value={flightNumber} onChange={(e) => setFlightNumber(e.target.value)} placeholder="e.g. AA1234" onKeyDown={(e) => e.key === "Enter" && handleFlightFilter()} />
                            </div>
                            <Button label="Apply Filter" icon="pi pi-filter" className={styles.secondaryButton} onClick={handleFlightFilter} />
                        </div>
                    </div>

                    {loadingInvoices ? (
                        <div className={styles.loadingState}>
                            <ProgressSpinner strokeWidth="4" />
                            <span>Loading outstanding invoices...</span>
                        </div>
                    ) : (
                        <>
                            <div className={styles.statsRow}>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <i className="pi pi-file" />
                                    </div>
                                    <div>
                                        <div className={styles.statValue}>{invoices.length}</div>
                                        <div className={styles.statLabel}>Outstanding Invoices</div>
                                    </div>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <i className="pi pi-wallet" />
                                    </div>
                                    <div>
                                        <div className={styles.statValue}>{formatMoney(totalOutstanding)}</div>
                                        <div className={styles.statLabel}>Total Owed</div>
                                    </div>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={`${styles.statIcon} ${styles.iconCredit}`}>
                                        <i className="pi pi-star" />
                                    </div>
                                    <div>
                                        <div className={styles.statValue}>{formatMoney(accountCreditBalance)}</div>
                                        <div className={styles.statLabel}>Existing Credit</div>
                                    </div>
                                </div>
                            </div>

                            {purchaseInvoiceCount > 0 && (
                                <div className={`${styles.purchaseNotice} ${unattachedPurchaseCount > 0 ? styles.purchaseNoticeWarn : ""}`}>
                                    <i className={`pi ${unattachedPurchaseCount > 0 ? "pi-exclamation-triangle" : "pi-shopping-cart"}`} />
                                    <span>
                                        {purchaseInvoiceCount} purchase invoice{purchaseInvoiceCount > 1 ? "s" : ""} outstanding
                                        {unattachedPurchaseCount > 0 ? ` - ${unattachedPurchaseCount} not linked to a package yet` : ""}
                                    </span>
                                </div>
                            )}

                            {invoices.length === 0 ? (
                                <div className={styles.sectionCard}>
                                    <div className={styles.emptyState}>This client has no outstanding invoices{flightNumber ? " for that flight number" : ""}.</div>
                                </div>
                            ) : (
                                <div className={styles.sectionCard}>
                                    <div className={styles.sectionCardHeader}>
                                        <h3>
                                            <i className="pi pi-money-bill" />
                                            Payment Details
                                        </h3>
                                    </div>

                                    <PaymentAllocationPanel
                                        key={panelKey}
                                        invoices={invoices}
                                        totalOutstanding={totalOutstanding}
                                        accountCreditBalance={accountCreditBalance}
                                        applyCredit={applyCredit}
                                        onApplyCreditChange={setApplyCredit}
                                        methodOptions={methodOptions}
                                        paymentMethod={paymentMethod}
                                        onPaymentMethodChange={(value) => {
                                            setPaymentMethod(value);
                                            if (value !== "cash") setCashRegisterId(null);
                                            else if (hasOwnRegister) setCashRegisterId(myRegisterId);
                                        }}
                                        cashRegisterOptions={hasOwnRegister ? undefined : cashRegisterOptions}
                                        cashRegisterId={cashRegisterId}
                                        onCashRegisterIdChange={setCashRegisterId}
                                        lockedCashRegisterLabel={hasOwnRegister ? myRegister?.name : undefined}
                                        actionLabel="Confirm & Process Payment"
                                        onAction={handlePanelAction}
                                        submitting={submitting}
                                    />

                                    <div className={styles.navRow}>
                                        <Button label="Cancel" className={styles.secondaryButton} onClick={startOver} disabled={submitting} />
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            {step === STEP.DONE && result && (
                <div className={styles.sectionCard}>
                    <div className={styles.completeSummary}>
                        <div className={styles.completeIcon}>
                            <i className="pi pi-check" />
                        </div>
                        <h4>Payment Recorded</h4>
                        <p>
                            {formatMoney(result.totalAllocated)} applied across {result.allocations.length} invoice(s) for {result.clientName}.
                            {result.creditAdded > 0 && ` ${formatMoney(result.creditAdded)} added to their credit balance.`}
                        </p>
                    </div>

                    <ResponsiveView value={result.allocations} emptyMessage="No allocations recorded." storageKey="recordPayment.result" renderCard={allocationCard} flush>
                        <DataTable value={result.allocations} className="datatable-responsive custom-datatable">
                            <Column field="invoiceId" header="Invoice" body={(row) => row.invoiceId || row.invoiceNo || "-"} />
                            <Column field="Waybills" header="Waybill" />
                            <Column field="shipmentNumber" header="Flight #" body={(row) => row.shipmentNumber || "-"} />
                            <Column header="Balance Before" body={(row) => formatMoney(row.balanceBefore)} />
                            <Column header="Applied" body={(row) => formatMoney(row.amountApplied)} />
                            <Column header="Balance After" body={(row) => formatMoney(row.balanceAfter)} />
                            <Column header="Status" body={statusBody} />
                        </DataTable>
                    </ResponsiveView>

                    <div className={styles.navRow}>
                        <Button label="Record Another Payment" icon="pi pi-plus" className={styles.primaryButton} onClick={startOver} />
                        <Button label="Back to Invoices" className={styles.secondaryButton} onClick={() => history.push("/invoices")} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecordPayment;
